import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  Subscriptions as SubscriptionsIcon,
  Security as SecurityIcon,
  Settings as SystemIcon,
  MarkEmailRead as ReadIcon,
  DoneAll as ReadAllIcon,
} from '@mui/icons-material';
import { fetchNotifications, markAsRead, markAllAsRead, type Notification } from '../store/slices/notificationsSlice';
import { RootState, AppDispatch } from '../store';

interface NotificationCenterProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ anchorEl, open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useSelector((state: RootState) => state.notifications);

  useEffect(() => {
    if (open) {
      dispatch(fetchNotifications());
    }
  }, [dispatch, open]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'payment':
        return <PaymentIcon />;
      case 'subscription':
        return <SubscriptionsIcon />;
      case 'security':
        return <SecurityIcon />;
      case 'system':
        return <SystemIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds}秒前`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}分前`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}時間前`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)}日前`;
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await dispatch(markAsRead(notification.id));
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllAsRead());
  };

  const recentNotifications = notifications.slice(0, 10);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 600,
          mt: 1,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
    >
      <Box>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            通知
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                color="error" 
                size="small" 
                sx={{ ml: 1, height: 20, '& .MuiChip-label': { fontSize: '0.75rem' } }}
              />
            )}
          </Typography>
          {unreadCount > 0 && (
            <IconButton size="small" onClick={handleMarkAllAsRead} title="すべて既読にする">
              <ReadAllIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Notifications List */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">読み込み中...</Typography>
            </Box>
          ) : recentNotifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">新しい通知はありません</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {recentNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      backgroundColor: notification.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                      '&:hover': {
                        backgroundColor: notification.isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.08)',
                      },
                      cursor: notification.actionUrl ? 'pointer' : 'default',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ 
                        color: `${getSeverityColor(notification.severity)}.main`,
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {getNotificationIcon(notification.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography 
                            variant="subtitle2" 
                            fontWeight={notification.isRead ? 400 : 600}
                            sx={{ mr: 1 }}
                          >
                            {notification.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatTimeAgo(notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />
                    {!notification.isRead && (
                      <Box sx={{ ml: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                        }} />
                      </Box>
                    )}
                  </ListItem>
                  {index < recentNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 10 && (
          <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
            <Button 
              size="small" 
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
            >
              すべての通知を表示
            </Button>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default NotificationCenter;